import { keyBy } from "lodash";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo } from "react";
import { usePrevious } from "react-use";
import { useBasePath } from "hooks/useBasePath";
import { LuBriefcase, LuPackage, LuPlane } from "react-icons/lu";

export type NavigationEntity = {
  id: string;
  name: string;
  relativePath: string;
  absolutePath?: string;
  icon?: React.ReactNode;
  children: NavigationEntity[];
  component?: React.FC | React.ComponentClass;
};

const flattenNavigationEntities = (
  entities: NavigationEntity[],
  parentPath: string,
): NavigationEntity[] => {
  return entities.flatMap((entity) => {
    let absolutePath =
      parentPath === "/"
        ? entity.relativePath
        : `${parentPath}${entity.relativePath}`;
    if (entity.relativePath === "/") {
      absolutePath = parentPath;
    }
    return [
      {
        ...entity,
        absolutePath,
      },
      ...flattenNavigationEntities(entity.children, absolutePath),
    ];
  });
};

type Options = {
  pushOnMount?: boolean;
};
const useNavigationEntities = (
  root: NavigationEntity,
  basePath: string,
  options?: Options,
) => {
  const pathname = usePathname();
  const router = useRouter();
  const { pushOnMount = true } = options ?? {};

  const flattenedEntities = useMemo(
    () => flattenNavigationEntities(root.children, basePath),
    [root, basePath],
  );

  const selectedNavigationEntity = useMemo(() => {
    const matchingEntities = flattenedEntities.filter((entity) => {
      return (
        entity.absolutePath != null && pathname.startsWith(entity.absolutePath)
      );
    });
    // sorted by length of absolute path in descending order.
    // The assumption here is that the longest absolute path is the most specific.
    const sortedEntities = matchingEntities.sort(
      (a, b) => (b.absolutePath?.length ?? 0) - (a.absolutePath?.length ?? 0),
    );
    return sortedEntities[0];
  }, [flattenedEntities, pathname]);

  useEffect(() => {
    if (!pushOnMount) {
      return;
    }

    const defaultPath = flattenedEntities[0].absolutePath;
    if (selectedNavigationEntity == null && defaultPath != null) {
      router.push(defaultPath);
    }
  }, [selectedNavigationEntity, flattenedEntities, router, pushOnMount]);

  return {
    root,
    all: flattenedEntities,
    byId: keyBy(flattenedEntities, "id"),
    selectedNavigationEntity,
  };
};

export function useIsSelected(entity: NavigationEntity) {
  const { selectedNavigationEntity } = useSidebarNavigationEntities();
  return entity.id === selectedNavigationEntity?.id;
}

const servicesNavigationRoot: NavigationEntity = {
  id: "root",
  name: "Root",
  relativePath: "/",
  children: [
    {
      id: "shipping",
      name: "Shipping",
      relativePath: "/",
      icon: <LuPackage />,
      children: [],
    },
    {
      id: "travel",
      name: "China with Gyasi",
      relativePath: "/travel",
      icon: <LuPlane />,
      children: [],
    },
  ],
};

export const useSidebarNavigationEntities = () => {
  const basePath = useBasePath();
  // Outside a known base path (e.g. public pages like /track), basePath is
  // null — entity paths would become "/null" and pushOnMount would redirect
  // there, so never push in that case.
  const entities = useNavigationEntities(
    servicesNavigationRoot,
    `/${basePath}`,
    { pushOnMount: basePath != null },
  );
  const previousSelectedEntity = usePrevious(entities.selectedNavigationEntity);

  return useMemo(
    () => ({
      ...entities,
      selectedNavigationEntity: entities.selectedNavigationEntity,
    }),
    [entities, previousSelectedEntity],
  );
};
